import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Inicio', path: '/' },
    { name: 'Empresa', path: '/empresa' },
    { name: 'Servicios', path: '/servicios' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contacto', path: '/contacto' },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="https://public.readdy.ai/ai/img_res/641cf114-66ad-406c-8c7e-b8b1bf5746b8.png"
                alt="PINTORDECO Barcelona Logo"
                className="h-12 w-auto object-contain"
              />
              <div className="flex flex-col">
                <span className={`font-bold text-lg ${isScrolled ? 'text-primary' : 'text-white'}`}>
                  PINTORDECO
                </span>
                <span className={`text-xs ${isScrolled ? 'text-gray-600' : 'text-white/90'}`}>
                  Barcelona
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`whitespace-nowrap font-medium transition-colors ${
                    location.pathname === link.path
                      ? isScrolled
                        ? 'text-accent'
                        : 'text-white'
                      : isScrolled
                      ? 'text-gray-700 hover:text-accent'
                      : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3">
              <a
                href="tel:+34690220944"
                className={`whitespace-nowrap px-4 py-2 rounded-full font-medium transition-all flex items-center space-x-2 cursor-pointer ${
                  isScrolled
                    ? 'text-primary hover:bg-primary/10'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                <i className="ri-phone-line"></i>
                <span>690 22 09 44</span>
              </a>
              <a
                href="https://wa.me/34690220944"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap px-5 py-2.5 bg-accent text-white rounded-full font-medium hover:bg-accent/90 transition-all flex items-center space-x-2 cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                <span>WhatsApp</span>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden p-2 rounded-lg cursor-pointer ${
                isScrolled ? 'text-primary' : 'text-white'
              }`}
            >
              <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden bg-white border-t border-gray-200"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2 font-medium ${
                    location.pathname === link.path ? 'text-accent' : 'text-gray-700'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 space-y-3">
                <a
                  href="tel:+34690220944"
                  className="whitespace-nowrap block w-full text-center px-5 py-3 bg-primary text-white rounded-full font-medium cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Llamar ahora
                </a>
                <a
                  href="https://wa.me/34690220944"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap block w-full text-center px-5 py-3 bg-accent text-white rounded-full font-medium cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </motion.nav>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/34690220944"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-accent rounded-full shadow-2xl flex items-center justify-center cursor-pointer hover:scale-110 transition-transform"
        style={{ animation: 'pulse 2s infinite' }}
      >
        <i className="ri-whatsapp-line text-white text-3xl"></i>
      </a>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </>
  );
};

export default Navbar;
